'use client';

import { useState, useEffect } from 'react';
import { useParams } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { PhoneNumberInput } from '@/components/phone-number-input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@/components/ui/input-otp';
import { Lock, Phone } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { toast } from 'sonner';
import { useTranslations } from 'next-intl';
import { normalizePhoneNumber } from '@/lib/utils';
import { denormalizeLocale, Locale } from '@/i18n/config';
import {
  getDefaultCountryCodeByLocale,
  PhoneCountryCode,
  splitPhoneNumber,
} from '@/lib/phone';
import { alternateSendViaSmsForResend } from '@/lib/otpPublicChannel';

const OTP_PHONE_STORAGE_KEY = 'otpVerifiedPhone';

/** Prevents duplicate onVerified calls when effect runs multiple times (e.g. React Strict Mode). */
let lastRestoredPhone: string | null = null;

type PhoneVerificationProps = {
  onVerified: (phone: string) => void | Promise<void>;
  onReset?: () => void;
  showChangePhoneButton?: boolean;
  phoneLabel?: string;
  phoneDescription?: string;
  autoFocus?: boolean;
  isBusy?: boolean;
  /** When set, pre-fills and locks the phone field (customer can't change it). */
  lockedPhone?: string;
};

export function PhoneVerification({
  onVerified,
  onReset,
  showChangePhoneButton = false,
  phoneLabel,
  phoneDescription,
  autoFocus = true,
  isBusy = false,
  lockedPhone,
}: PhoneVerificationProps) {
  const t = useTranslations();
  const params = useParams();
  const locale = params.locale as string;
  const storeId = typeof params.storeId === 'string' ? params.storeId : undefined;

  const [phone, setPhone] = useState('');
  const [countryCode, setCountryCode] = useState<PhoneCountryCode>(() =>
    getDefaultCountryCodeByLocale(locale)
  );

  // Update country code when locale changes
  useEffect(() => {
    setCountryCode(getDefaultCountryCodeByLocale(locale));
  }, [locale]);

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [otpCode, setOtpCode] = useState('');
  const [isSendingOTP, setIsSendingOTP] = useState(false);
  const [isVerifyingOTP, setIsVerifyingOTP] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [didNotReceiveCooldownSec, setDidNotReceiveCooldownSec] = useState(0);

  useEffect(() => {
    if (didNotReceiveCooldownSec <= 0) return;
    const id = window.setInterval(() => {
      setDidNotReceiveCooldownSec((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [didNotReceiveCooldownSec]);

  const validatePhone = (phoneValue: string): string | null => {
    if (!phoneValue.trim()) {
      return t('checkout.validation.phoneRequired');
    }
    const digitsOnly = phoneValue.replace(/\D/g, '');
    if (digitsOnly.length < 10) {
      return t('checkout.validation.phoneInvalid');
    }
    return null;
  };

  const getNormalizedPhone = (
    phoneValue: string = phone,
    phoneCountryCode: PhoneCountryCode = countryCode
  ): string => {
    const trimmedPhone = phoneValue.trim();
    if (!trimmedPhone) {
      return '';
    }
    const normalizedLocale = denormalizeLocale(locale) as Locale;
    return normalizePhoneNumber(
      `${phoneCountryCode}${trimmedPhone}`,
      normalizedLocale
    );
  };

  const applyNormalizedPhone = (normalizedPhone: string) => {
    const { countryCode: nextCountryCode, localNumber } = splitPhoneNumber(
      normalizedPhone,
      getDefaultCountryCodeByLocale(locale)
    );
    setCountryCode(nextCountryCode);
    setPhone(localNumber);
  };

  useEffect(() => {
    if (lockedPhone) {
      applyNormalizedPhone(lockedPhone);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lockedPhone]);

  const checkOtpSession = async (phoneValue: string): Promise<boolean> => {
    if (!phoneValue) return false;
    const normalizedLocale = denormalizeLocale(locale) as Locale;
    const normalizedPhone = normalizePhoneNumber(phoneValue, normalizedLocale);
    try {
      const response = await fetch(
        `/api/otp/session?phone=${encodeURIComponent(normalizedPhone)}&locale=${locale}`,
        {
          cache: 'no-store',
        }
      );
      if (!response.ok) {
        return false;
      }
      const data = await response.json();
      return Boolean(data.valid);
    } catch (error) {
      console.error('Error checking OTP session:', error);
      return false;
    }
  };

  const attemptSessionBypass = async (phoneValue: string): Promise<boolean> => {
    const sessionValid = await checkOtpSession(phoneValue);
    if (sessionValid) {
      const normalizedLocale = denormalizeLocale(locale) as Locale;
      const normalizedPhone = normalizePhoneNumber(phoneValue, normalizedLocale);

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(OTP_PHONE_STORAGE_KEY, normalizedPhone);
      }
      setIsAuthenticated(true);
      setStep('phone');
      setOtpCode('');
      applyNormalizedPhone(normalizedPhone);
      await onVerified(normalizedPhone);
      return true;
    }
    return false;
  };

  const handleSendOTP = async (sendViaSms?: boolean) => {
    if (isBusy) {
      return;
    }
    const phoneError = validatePhone(phone);
    if (phoneError) {
      toast.error(phoneError);
      setPhoneError(phoneError);
      return;
    }

    const normalizedPhone = getNormalizedPhone();

    if (await attemptSessionBypass(normalizedPhone)) {
      return;
    }

    setIsSendingOTP(true);
    setPhoneError(null);

    try {
      const response = await fetch('/api/otp/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: normalizedPhone,
          locale: locale,
          ...(sendViaSms !== undefined && { sendViaSms }),
          ...(storeId && { storeId }),
        }),
      });

      if (!response.ok) {
        const data = await response.json();

        // Handle rate limit error specifically
        if (response.status === 429 && data.rateLimitRemaining) {
          const errorMessage = t('otp.rateLimit', { seconds: data.rateLimitRemaining });
          toast.error(errorMessage);
          setPhoneError(errorMessage);
          throw new Error(errorMessage);
        }

        throw new Error(data.error || 'Failed to send OTP');
      }

      toast.success(
        sendViaSms === true ? 'Codigo enviado por SMS' : t('otp.codeSent')
      );
      setDidNotReceiveCooldownSec(30);
      setStep('otp');
    } catch (error) {
      console.error('Error sending OTP:', error);
      const errorMessage = error instanceof Error ? error.message : (t('otp.sendError') || t('errors.generic'));

      // Only show generic error if it's not already a specific error message
      if (!phoneError) {
        toast.error(errorMessage);
        setPhoneError(errorMessage);
      }
    } finally {
      setIsSendingOTP(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otpCode.length !== 6) {
      toast.error(t('otp.invalidCode'));
      return;
    }

    setIsVerifyingOTP(true);

    const normalizedPhone = getNormalizedPhone();

    try {
      const response = await fetch('/api/otp/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phone: normalizedPhone,
          code: otpCode,
          locale: locale,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to verify OTP');
      }

      const data = await response.json();

      if (!data.valid) {
        toast.error(data.message || t('otp.invalidCode'));
        return;
      }

      toast.success(t('otp.codeVerified'));
      setIsAuthenticated(true);
      setStep('phone');
      setOtpCode('');
      applyNormalizedPhone(normalizedPhone);

      if (typeof window !== 'undefined') {
        window.localStorage.setItem(OTP_PHONE_STORAGE_KEY, normalizedPhone);
      }

      await onVerified(normalizedPhone);
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error(t('otp.verifyError') || t('errors.generic'));
    } finally {
      setIsVerifyingOTP(false);
    }
  };

  const resetPhoneAuth = async () => {
    // Clear server-side session cookie
    try {
      await fetch('/api/otp/logout', {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Error clearing session:', error);
      // Continue with local cleanup even if API call fails
    }

    // Clear local state
    setIsAuthenticated(false);
    setStep('phone');
    setOtpCode('');
    setPhoneError(null);
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem(OTP_PHONE_STORAGE_KEY);
    }
    if (onReset) {
      onReset();
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const storedPhone = window.localStorage.getItem(OTP_PHONE_STORAGE_KEY);
    if (!storedPhone) return;

    const normalizedLocale = denormalizeLocale(locale) as Locale;
    const normalizedPhone = normalizePhoneNumber(storedPhone, normalizedLocale);

    // When a phone is locked, reject stored sessions that belong to a different phone.
    if (lockedPhone) {
      const normalizedLocked = normalizePhoneNumber(lockedPhone, normalizedLocale);
      if (normalizedPhone !== normalizedLocked) {
        window.localStorage.removeItem(OTP_PHONE_STORAGE_KEY);
        lastRestoredPhone = null;
        return;
      }
    }

    applyNormalizedPhone(normalizedPhone);

    if (normalizedPhone !== storedPhone) {
      window.localStorage.setItem(OTP_PHONE_STORAGE_KEY, normalizedPhone);
    }

    checkOtpSession(normalizedPhone).then(async (valid) => {
      if (valid) {
        if (lastRestoredPhone === normalizedPhone) return;
        lastRestoredPhone = normalizedPhone;
        setIsAuthenticated(true);
        setStep('phone');
        setOtpCode('');
        await onVerified(normalizedPhone);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, lockedPhone]);

  if (step === 'phone') {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            {phoneLabel || t('checkout.phoneNumber')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {phoneDescription && (
            <p className="text-muted-foreground mb-6">
              {phoneDescription}
            </p>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!isSendingOTP && !isBusy) {
                handleSendOTP();
              }
            }}
            className="space-y-4"
          >
            <div>
              <Label htmlFor="phone" className="flex items-center gap-1.5">
                {lockedPhone ? (
                  <>
                    <Lock className="w-3.5 h-3.5 text-muted-foreground" />
                    {t('checkout.phoneLockedLabel')}
                  </>
                ) : (
                  t('checkout.phoneNumber')
                )}
              </Label>
              <PhoneNumberInput
                id="phone"
                name="phone"
                autoComplete="tel"
                placeholder={t('checkout.phonePlaceholder')}
                value={phone}
                onChange={(value) => {
                  if (lockedPhone) return;
                  setPhone(value);
                  setPhoneError(null);
                }}
                countryCode={countryCode}
                onCountryCodeChange={(code) => {
                  if (lockedPhone) return;
                  setCountryCode(code);
                }}
                getCountryLabel={(code) => {
                  switch (code) {
                    case "+55":
                      return t('countries.brazil')
                    case "+1":
                      return t('countries.unitedStates')
                    default:
                      return code
                  }
                }}
                className="mt-2"
                inputClassName={phoneError ? 'border-red-500' : undefined}
                disabled={isAuthenticated || isSendingOTP || !!lockedPhone}
                readOnly={isAuthenticated || !!lockedPhone}
                autoFocus={autoFocus && !lockedPhone}
              />
              {phoneError && (
                <p className="text-red-600 text-sm mt-1">{phoneError}</p>
              )}
            </div>
            {!isAuthenticated && (
              <Button
                type="submit"
                disabled={isSendingOTP || isBusy}
                className="w-full"
              >
                {isSendingOTP ? (
                  <>
                    <Spinner />
                    {t('otp.sendingCode')}
                  </>
                ) : (
                  t('otp.sendCode')
                )}
              </Button>
            )}
            {isAuthenticated && showChangePhoneButton && (
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={resetPhoneAuth}
              >
                {t('orders.changePhone')}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    );
  }

  if (step === 'otp' && !isAuthenticated) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Phone className="w-5 h-5" />
            {t('otp.title')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground mb-6">
            {t('otp.description')}
          </p>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (!isVerifyingOTP && otpCode.length === 6) {
                handleVerifyOTP();
              }
            }}
            className="space-y-4"
          >
            <div className="flex flex-col items-center space-y-4">
              <InputOTP
                maxLength={6}
                value={otpCode}
                onChange={(value) => setOtpCode(value)}
                disabled={isVerifyingOTP}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                </InputOTPGroup>
                <InputOTPSeparator />
                <InputOTPGroup>
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>
            <div className="flex flex-col gap-3">
              <Button
                type="submit"
                disabled={isVerifyingOTP || otpCode.length !== 6}
                className="w-full"
              >
                {isVerifyingOTP ? (
                  <>
                    <Spinner />
                    {t('otp.verifying')}
                  </>
                ) : (
                  t('otp.verifyCode')
                )}
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setStep('phone');
                    setOtpCode('');
                    setDidNotReceiveCooldownSec(0);
                  }}
                  disabled={isSendingOTP || isVerifyingOTP}
                  className="flex-1"
                >
                  {t('otp.resendCode')}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleSendOTP(alternateSendViaSmsForResend())}
                  disabled={
                    isSendingOTP ||
                    isVerifyingOTP ||
                    isBusy ||
                    didNotReceiveCooldownSec > 0
                  }
                  className="flex-1"
                >
                  {t('otp.didNotReceive')}
                  {didNotReceiveCooldownSec > 0
                    ? ` ${t('otp.didNotReceiveCountdown', { seconds: didNotReceiveCooldownSec })}`
                    : ''}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    );
  }

  return null;
}

